/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC } from "react"
import { ChevronDownUniversal } from "@duxton-web/icons"

import styles from "./PickerField.modules.css"

interface PickerFieldProps {
  /** Ties the field to its own `<label htmlFor>`, as the `Select` it replaces was. */
  id: string
  /** Committed display text. Absent renders the placeholder instead. */
  value?: string
  placeholder: string
  /** Whether this field's sheet is currently open — announced, not just drawn. */
  isOpen: boolean
  onOpen(): void
}

/**
 * The closed state of a narrow-width picker: looks like the `Select` it stands in for, opens
 * an `OptionPickerSheet` instead of a dropdown.
 *
 * Shared by the review and inspection cards, which render four of these between them. It was
 * duplicated per card first, and the copies were byte-identical down to the CSS — one design
 * nudge away from drifting apart.
 */
export const PickerField: FC<PickerFieldProps> = ({
  id,
  value,
  placeholder,
  isOpen,
  onOpen,
}) => (
  <button
    type="button"
    id={id}
    className={styles.field}
    onClick={onOpen}
    /*
     * The `Select` this replaces announced that it opened something and whether it was
     * expanded; a bare button says neither, so the narrow path would silently lose it for
     * screen-reader users.
     *
     * `dialog`, not `listbox`: what opens is an `OptionPickerSheet` — a `Drawer` with a
     * radio group inside — so promising a listbox would describe an interaction (arrow
     * keys moving an active option, `aria-activedescendant`) that this does not implement.
     */
    aria-haspopup="dialog"
    aria-expanded={isOpen}
  >
    <span className={value ? styles.fieldValue : styles.fieldPlaceholder}>
      {value ?? placeholder}
    </span>
    {/* Decorative — the button's text already names it, so it is not announced twice. */}
    <ChevronDownUniversal className={styles.fieldChevron} aria-hidden />
  </button>
)
